import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import type { CapturedErrorTypes } from "@hz/common/constants";
import { errorStates } from "@hz/common/constants";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { api } from "@state/apiSlice";
import type { ErrorState } from "@state/errorSlice";
import { clearApiErrors, selectApiErrors } from "@state/errorSlice";

import ErrorBoundaryContent from "./error-boundary-content";

// Define a list of API requests and Redux thunks that require special error handling.
// These are considered critical because their failure affects core application functionality.
// - API requests (e.g., `POST /api/sync-contact`) trigger specific actions upon failure.
// - Thunk methods (e.g., `monday/fetchMondayContext/rejected`) represent failed Redux operations.
const ADDITIONAL_EXCEPTION_REQUESTS = [
  { method: "POST", endpoint: "/api/sync-contact" },
  { method: "THUNK", endpoint: "monday/fetchMondayContext/rejected" },
];

// Helper function to check if an error matches the additional exception list.
const isAdditionalExceptionRequest = (error: ErrorState) => {
  return ADDITIONAL_EXCEPTION_REQUESTS.some(
    (exception) => exception.method === error.query?.method && exception.endpoint === error.query.endpoint,
  );
};

// Type definition for categorized errors.
type CapturedError = {
  type: CapturedErrorTypes;
  error: { status?: number; eventId?: string };
};

/**
 * Helper function to categorize and retrieve the most relevant error.
 * It prioritizes:
 * 1. **401 Unauthorized** errors (triggers a redirect to the reconnection page).
 * 2. **404 Xero Not Connected** errors (triggers a redirect to the connections page).
 * 3. **Other API errors** (displays the error page).
 */
const getCapturedError = (errors: ErrorState[]): CapturedError | null => {
  // Prioritize 401 Unauthorized errors (redirects the user).

  const googleReauthRequiredError = errors.find(
    (error) => error.status === 401 && error.data?.code === errorStates.GOOGLE_REAUTH_REQUIRED,
  );
  if (googleReauthRequiredError) {
    return { type: errorStates.GOOGLE_REAUTH_REQUIRED, error: googleReauthRequiredError };
  }

  const noPermissionError = errors.find((error) => error.status === 403);
  if (noPermissionError) {
    return { type: errorStates.NO_PERMISSION, error: noPermissionError };
  }

  const noSubscriptionError = errors.find(
    (error) => error.status === 402 && error.data?.type === errorStates.NO_SUBSCRIPTION,
  );
  if (noSubscriptionError) {
    return { type: errorStates.NO_SUBSCRIPTION, error: noSubscriptionError };
  }

  const seatsUpgradeRequiredError = errors.find(
    (error) => error.status === 402 && error.data?.type === errorStates.SEATS_UPGRADE_REQUIRED,
  );
  if (seatsUpgradeRequiredError) {
    return { type: errorStates.SEATS_UPGRADE_REQUIRED, error: seatsUpgradeRequiredError };
  }

  // Capture other API errors (GET requests or explicitly listed exceptions).
  const otherError = errors.find(
    (error) => error.query?.method === "GET" || isAdditionalExceptionRequest(error),
  );
  if (otherError) {
    return { type: errorStates.OTHER, error: otherError };
  }

  return null; // No relevant error found.
};

/**
 * **ApiQueryErrorBoundary**
 *
 * This component serves as an error boundary for handling API-related errors.
 * It intercepts errors captured by `errorMiddleware.ts` and categorizes them to determine
 * the appropriate action (e.g., redirecting or displaying an error page).
 *
 * **How it Works:**
 * - Errors are collected globally via `errorMiddleware.ts` and stored in `@state/errorSlice`.
 * - This component checks for known error conditions and handles them accordingly:
 *   - **401 Unauthorized** → Redirects to `/settings/connections?forceReconnect=true`.
 *   - **404 Xero Not Connected** → Redirects to `/settings/connections`.
 *   - **Other Errors** → Displays an error page (`ErrorBoundaryContent`).
 * - If no errors exist, the app renders normally.
 *
 * **Features:**
 * - Clears errors after handling to prevent repeated redirects.
 * - Supports retrying by resetting application state and API cache.
 *
 * @param {ReactNode} children - Components to render when no errors occur.
 *
 * @example
 * <ApiQueryErrorBoundary>
 *   <YourComponent />
 * </ApiQueryErrorBoundary>
 */
export default function ApiQueryErrorBoundary({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const errors = useSelector(selectApiErrors);

  const [currentError, setCurrentError] = useState<{
    type?: CapturedErrorTypes;
    status?: number;
    eventId?: string;
  } | null>(null);

  useEffect(() => {
    const error = getCapturedError(errors);

    switch (error?.type) {
      case errorStates.GOOGLE_REAUTH_REQUIRED:
        navigate("/reconnect");
        dispatch(clearApiErrors());
        break;

      case errorStates.NO_PERMISSION:
        // "Access Denied: Your Xero for monday User Lacks the Required Permissions",
        setCurrentError({
          type: errorStates.NO_PERMISSION,
          status: error.error.status,
          eventId: error.error.eventId,
        });
        break;
      case errorStates.NO_SUBSCRIPTION:
        setCurrentError({
          type: errorStates.NO_SUBSCRIPTION,
          status: error.error.status,
          eventId: error.error.eventId,
        });
        break;
      case errorStates.SEATS_UPGRADE_REQUIRED:
        setCurrentError({
          type: errorStates.SEATS_UPGRADE_REQUIRED,
          status: error.error.status,
          eventId: error.error.eventId,
        });
        break;
      case errorStates.OTHER:
        // Store the first valid error to display an error page.
        setCurrentError({
          status: error.error.status,
          eventId: error.error.eventId,
        });
        break;

      default:
        // No relevant error found, clear the current error.
        console.log("ran default");
        setCurrentError(null);
        break;
    }
  }, [errors, navigate, dispatch]);

  const onRetry = () => {
    // Reset application state and clear errors, allowing the user to try again.
    dispatch(api.util.resetApiState());
    dispatch(clearApiErrors());
    setCurrentError(null);
  };

  // If an error exists, display the error boundary page.
  if (currentError) {
    return (
      <ErrorBoundaryContent
        type={currentError.type || "API_ERROR"}
        onRetry={onRetry}
        eventId={currentError.eventId}
      />
    );
  }

  // Render the child components if no critical errors are detected.
  return children;
}
